import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, AlertTriangle, Trash2, Archive, ShieldCheck } from "lucide-react";

// -------------------------------------------------------------- labels
const CATEGORY_LABELS = {
  websites:            "Websites",
  contacts:            "Contacts",
  leads:               "Leads",
  enquiries:           "Enquiries",
  scans:               "Scans",
  business_services:   "Service interests",
  quotes:              "Quotes",
  prospects:           "Prospects",
  opportunities:       "Opportunities",
  campaign_recipients: "Campaign recipients",
  communications:      "Communications",
  activities:          "Activity log entries",
  subscriptions:       "Subscriptions",
  invoices:            "Invoices",
  agreements:          "Agreements",
  payments:            "Payments",
  monitoring_profiles: "Monitoring profiles",
  projects:            "Projects",
};

function labelFor(key) {
  return CATEGORY_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function SummaryRow({ label, valueLabel, tone = "neutral", testId }) {
  const color = tone === "danger" ? "text-[#C1350B]" : tone === "retain" ? "text-[#0FA958]" : "text-[#0A0A0A]";
  return (
    <li className="flex items-center justify-between border-b border-[#EAEAEA] py-1.5" data-testid={testId}>
      <span className="uppercase text-[10px] tracking-[0.18em] font-bold text-[#8A8A8A]">{label}</span>
      <span className={`font-black text-[12px] ${color}`}>{valueLabel}</span>
    </li>
  );
}

/** Delete / Archive / Permanent-Delete confirmation for a SINGLE client. */
export function DeleteClientModal({ open, businessId, businessName, onClose, onDone }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !businessId) return;
    setPlan(null);
    hqApi.get(`/control/clients/${businessId}/deletion-plan`)
      .then((r) => setPlan(r.data))
      .catch(() => toast.error("Could not load deletion plan"));
  }, [open, businessId]);

  if (!open) return null;

  const action = plan?.action; // "archive" | "delete" | "permanent_delete"
  const isPermanent = action === "permanent_delete";
  const isArchive   = action === "archive";

  const successToast = () => {
    if (isPermanent) return "Client permanently deleted.";
    if (isArchive)   return "Client archived.";
    return "Client deleted.";
  };

  const run = async () => {
    setBusy(true);
    try {
      await hqApi.delete(`/control/clients/${businessId}`, { data: {} });
      toast.success(successToast());
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };

  // ---------- header wording ----------
  const headerEyebrow =
    isPermanent ? "Delete client permanently" :
    isArchive   ? "Archive client"            :
                  "Archive client";

  const headerIcon =
    isPermanent ? <Trash2 className="text-[#C1350B]" size={20}/> :
    isArchive   ? <Archive className="text-[#FF6A00]" size={20}/> :
                  <Archive className="text-[#FF6A00]" size={20}/>;

  const headerIconBg = isPermanent ? "#FFECEC" : "#FFF7EE";

  // ---------- CTA styling / label ----------
  const ctaLabel =
    isPermanent ? "Delete permanently" :
    isArchive   ? "Archive client"     :
                  "Archive client";

  const ctaCls = isPermanent
    ? "bg-[#C1350B] text-white"
    : "bg-[#FF6A00] text-black";

  // ---------- body ----------
  const renderPermanentBody = () => {
    const removable = plan?.breakdown?.removable || {};
    const retained  = plan?.breakdown?.retained  || {};
    const removableEntries = Object.entries(removable).filter(([, v]) => v > 0);
    const retainedEntries  = Object.entries(retained).filter(([, v]) => v > 0);
    return (
      <div data-testid="hq-client-permanent-body">
        <div className="p-3 rounded-xl bg-[#FFECEC] border border-[#F3B9A2]">
          <p className="text-[13px] text-[#8B2A0F]">
            <strong>This client is already archived.</strong> Permanently deleting this client will remove the client and its associated removable records from OrangeBySite. <strong>This action cannot be undone.</strong>
          </p>
        </div>

        <div className="mt-4">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Client profile</div>
          <SummaryRow
            label="Client profile"
            valueLabel="Will be permanently deleted"
            tone="danger"
            testId="hq-client-perm-summary-profile"
          />
        </div>

        {removableEntries.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Removable linked records</div>
            <ul className="mt-1" data-testid="hq-client-perm-summary-removable">
              {removableEntries.map(([k, v]) => (
                <SummaryRow
                  key={k}
                  label={labelFor(k)}
                  valueLabel={`${v} removable`}
                  tone="danger"
                  testId={`hq-client-perm-remove-${k}`}
                />
              ))}
            </ul>
          </div>
        )}

        {retainedEntries.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] inline-flex items-center gap-1">
              <ShieldCheck size={11}/> Retained for legal / financial compliance
            </div>
            <ul className="mt-1" data-testid="hq-client-perm-summary-retained">
              {retainedEntries.map(([k, v]) => (
                <SummaryRow
                  key={k}
                  label={labelFor(k)}
                  valueLabel={`${v} historical record${v === 1 ? "" : "s"} retained`}
                  tone="retain"
                  testId={`hq-client-perm-retain-${k}`}
                />
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-[#8A8A8A]">
              These records must remain for financial, legal or audit reasons. Their business reference is preserved for traceability.
            </p>
          </div>
        )}

        {removableEntries.length === 0 && retainedEntries.length === 0 && (
          <p className="mt-3 text-[13px] text-[#4A4A4A]" data-testid="hq-client-perm-nothing">
            No linked records — only the client profile will be removed.
          </p>
        )}
      </div>
    );
  };

  const renderArchiveBody = () => (
    <div>
      <p>This client has protected linked history and will be <strong>archived</strong>, not permanently deleted.</p>
      <ul className="mt-3 space-y-1 text-[13px] text-[#4A4A4A]">
        {Object.entries(plan.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => (
          <li key={k} className="flex justify-between border-b border-[#EAEAEA] py-1">
            <span className="uppercase text-[10px] tracking-[0.18em] font-bold text-[#8A8A8A]">{labelFor(k)}</span>
            <span className="font-black">{v}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-[#8A8A8A]">Financial and compliance records remain untouched.</p>
    </div>
  );

  const renderDeleteBody = () => (
    <div>
      <p>This client will be moved to the <strong>Archived</strong> list. It can be permanently deleted from there when you're sure it's no longer needed.</p>
      <p className="mt-2 text-[12px] text-[#8A8A8A]">Financial and compliance records remain untouched.</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="hq-client-delete-modal" data-action={action || "loading"}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full" style={{ background: headerIconBg }}>
              {headerIcon}
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]" data-testid="hq-client-delete-eyebrow">{headerEyebrow}</div>
              <h3 className="mt-1 font-black text-xl" data-testid="hq-client-delete-name">
                {businessName || plan?.business_name || "This client"}
              </h3>
            </div>
          </div>
          <button onClick={onClose} data-testid="hq-client-delete-close" className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>
        <div className="p-5 text-sm">
          {!plan ? (
            <div className="text-[#8A8A8A]" data-testid="hq-client-delete-loading">Reviewing linked records…</div>
          ) : isPermanent ? renderPermanentBody()
            : isArchive   ? renderArchiveBody()
            :               renderDeleteBody()}
        </div>
        <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
          <button onClick={onClose} data-testid="hq-client-delete-cancel" className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
          <button
            onClick={run}
            disabled={busy || !plan}
            data-testid="hq-client-delete-confirm"
            className={`px-5 py-2 rounded-full text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50 ${ctaCls}`}
          >
            {busy ? "Working…" : ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Confirmation for BULK archive (active clients) OR permanent delete
 *  (archived clients). The wording, confirm gate and CTA colour are
 *  driven by `filterKey`:
 *    - "archived": destructive path — requires typing DELETE, red CTA.
 *    - anything else: soft archive — no typing, orange CTA. */
export function BulkDeleteModal({ open, businessIds, filterKey, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const isPermanent = filterKey === "archived";
  // Phase 3I.4P.3.2 — active-client bulk archive never requires typing
  // DELETE. Only the permanent-delete path (archived filter) does.
  const needsType = isPermanent;

  // Reset transient state whenever the modal closes/opens so a previous
  // confirmation attempt can't linger between sessions.
  useEffect(() => { setConfirm(""); setBusy(false); }, [open]);

  if (!open) return null;

  const run = async () => {
    if (needsType && confirm.trim().toUpperCase() !== "DELETE") return toast.error("Type DELETE to confirm");
    setBusy(true);
    try {
      const r = await hqApi.post("/control/clients/bulk-delete", {
        business_ids: businessIds, confirm: needsType ? confirm : "DELETE",
      });
      const d = r.data || {};
      const parts = [];
      if (d.permanently_deleted) parts.push(`${d.permanently_deleted} permanently deleted`);
      if (d.deleted) parts.push(`${d.deleted} deleted`);
      if (d.archived) parts.push(`${d.archived} archived`);
      if (d.not_found) parts.push(`${d.not_found} not found`);
      if (d.failed) parts.push(`${d.failed} failed`);
      toast.success(parts.join(" · ") || "Done");
      onDone && onDone(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
    setBusy(false); setConfirm("");
  };

  const eyebrow  = isPermanent ? "Permanently delete clients" : "Archive clients";
  const iconBg   = isPermanent ? "bg-[#FFECEC]" : "bg-[#FFF7EE]";
  const iconEl   = isPermanent
    ? <AlertTriangle className="text-[#C1350B]" size={20}/>
    : <Archive className="text-[#FF6A00]" size={20}/>;
  const headline = isPermanent
    ? `You are about to permanently delete ${businessIds.length} archived client record${businessIds.length === 1 ? "" : "s"}`
    : `You are about to archive ${businessIds.length} client${businessIds.length === 1 ? "" : "s"}`;
  const bodyCopy = isPermanent
    ? "Permanently deleting will remove the client profile and its removable linked records. Financial, legal and audit history is retained for compliance. This action cannot be undone."
    : "Archived clients are removed from the active Clients view but remain available through the Archived filter. Nothing is permanently deleted.";
  const ctaLabel = isPermanent
    ? `Delete permanently ${businessIds.length}`
    : `Archive ${businessIds.length} client${businessIds.length === 1 ? "" : "s"}`;
  const ctaCls   = isPermanent
    ? "bg-[#C1350B] text-white"
    : "bg-[#FF6A00] text-black";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="hq-client-bulk-modal" data-mode={isPermanent ? "permanent" : "archive"}>
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-full ${iconBg}`}>{iconEl}</div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{eyebrow}</div>
              <h3 className="mt-1 font-black text-xl" data-testid="hq-client-bulk-headline">{headline}</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>
        <div className="p-5 text-sm">
          <p>{bodyCopy}</p>
          {needsType && (
            <label className="block mt-4">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Type <strong>DELETE</strong> to confirm</div>
              <input data-testid="hq-client-bulk-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" className="w-full px-3 py-2 rounded-lg border"/>
            </label>
          )}
        </div>
        <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
          <button onClick={run}
            disabled={busy || (needsType && confirm.trim().toUpperCase() !== "DELETE")}
            data-testid="hq-client-bulk-confirm"
            className={`px-5 py-2 rounded-full text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50 ${ctaCls}`}>
            {busy ? "Working…" : ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
